import React from "react";

import { Progress } from "@/components/ui/progress";

import { JobRequest } from "../../models/Jobs/JobRequest";
import { JobRequestStatus } from "../../models/Jobs/JobRequestStatus";

/** A rough duration, to the minute: "under a minute", "12 min", "2 h 5 min". */
export const formatRoughDuration = (milliseconds: number): string => {
  const minutes = Math.round(milliseconds / 60_000);
  if (minutes < 1) return "under a minute";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
};

/** Whether there is progress worth showing: the job reports it, and has started without simply completing. */
export const hasJobProgress = (job: JobRequest): boolean =>
  (job.progressTotal ?? 0) > 0 &&
  job.status !== JobRequestStatus.Pending &&
  job.status !== JobRequestStatus.Completed;

/**
 * How far a job has got: items processed out of the total, how long it has been running, and roughly how long is
 * left.
 */
/*
 * The figures come from the job's row, which the server updates about every 30 seconds while the job runs, so they are
 * as fresh as the list's last refresh. The estimate assumes the rest goes at the average pace so far, and the pace
 * counts the whole time since the job first started, retries and restarts included.
 */
const JobProgress: React.FC<{ job: JobRequest }> = ({ job }) => {
  if (!hasJobProgress(job)) return null;

  const total = job.progressTotal ?? 0;
  const done = Math.min(job.progressDone ?? 0, total);
  const running = job.status === JobRequestStatus.Processing && !!job.startedAt;
  const elapsed = running ? Date.now() - new Date(job.startedAt as string).getTime() : null;
  const remaining = elapsed != null && done > 0 && done < total ? (elapsed / done) * (total - done) : null;

  return (
    <div className="flex basis-full flex-col gap-1 min-w-40">
      <Progress value={(done / total) * 100} className="h-1.5" aria-label="Progress" />
      <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
        <span>
          {done.toLocaleString()} of {total.toLocaleString()} processed
        </span>
        {elapsed != null && <span>running for {formatRoughDuration(elapsed)}</span>}
        {remaining != null && <span>about {formatRoughDuration(remaining)} left</span>}
      </div>
    </div>
  );
};

export default JobProgress;
